import {
    IonButton,
    IonCard,
    IonCardContent,
    IonCol,
    IonContent,
    IonIcon,
    IonPage,
    IonRow
} from "@ionic/react";
import React, { useContext, useEffect, useState } from "react";
import { Competition, Person } from "../../../../../../models";
import Header from "../../../../../../components/Headers/Header";
import PageTitle from "../../../../../../components/PageTitle/PageTitle";
import { PersonContext } from "../../../../../../context/PersonContext";
import { getCompetitionsByTeamId } from "../../../../../../utilities/competition/Competition";
import Spinner from "../../../../../../components/Spinners/Spinner";
import { Table } from "reactstrap";
import { formatStartEndDatesStrings } from "../../../../../../utilities/dates/FormatDates";
import { mailSharp } from "ionicons/icons";
import { generateAELCompetitionInvitationReport } from "../../../../../../utilities/reports/CompetitionReport";
import moment from "moment";
import InformationBanner from "../../../../../../components/Banners/InformationBanner";
import { Link, useHistory } from "react-router-dom";
import { getTeamsForCurrentUser } from "../../../../../../utilities/team/TeamUtils";
import { useSubscriptionByItself } from "../../../../../../utilities/subscription/Subscription";
import { onCreateCompetition } from "../../../../../../graphql/subscriptions";
import { getPersonByPersonId } from "../../../../../../utilities/person/Person";
import { formatDisplayNameForUser } from "../../../../../../utilities/person/PersonNameFormat";
import ErrorBanner from "../../../../../../components/Banners/ErrorBanner";
import { getCompetitionStatus, getCompetitionStatusColor } from "../../../../../../utilities/competition/CompetitionUtils";

interface FormattedCompetition {
    id: string;
    startDate: string;
    name: string;
    createdBy: string;
    status: string;
    statusColor: string;
    updatedOn: string;
    competition: Competition;
}

const TeamCompetitionsPage: React.FC = () => {
    const user = useContext(PersonContext);

    const createCompetitionSubscription = useSubscriptionByItself({
        config: {
            query: onCreateCompetition,
            key: "onCreateCompetition"
        }
    });

    const [currentSubscriptionItem, setCurrentSubscriptionItem] = useState<any>();
    const [formattedCompetitions, setFormattedCompetitions] = useState<FormattedCompetition[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        getCompetitions();
    }, []);

    useEffect(() => {
        if (createCompetitionSubscription && createCompetitionSubscription[0] !== undefined && createCompetitionSubscription[0] !== currentSubscriptionItem) {
            setCurrentSubscriptionItem(createCompetitionSubscription[0]);
            getCompetitions();
        }
    }, [createCompetitionSubscription]);

    const getCompetitions = async () => {
        setIsLoading(true);
        setError("");
        let list: Competition[] = [];

        // Get all teams for this user
        const teams = await getTeamsForCurrentUser(user, false);
        console.log("teams", teams);
        if (teams && teams.length > 0) {
            for (let i = 0; i < teams.length; i++) {
                const team = teams[i];
                const competitionQuery = await getCompetitionsByTeamId(team.id);
                if (competitionQuery.isSuccess) {
                    const competitionsForTeam: Competition[] = competitionQuery.result;
                    list = list.concat(competitionsForTeam);
                }
            }
        }
        await formatCompetitions(list);
        setIsLoading(false);
    }

    const formatCompetitions = async (currentCompetitions: Competition[]) => {
        const formattedCompetitions: FormattedCompetition[] = [];
        for (let i = 0; i < currentCompetitions.length; i++) {
            const currentCompetition = currentCompetitions[i];

            // First, format the competition status
            let competitionStatus = getCompetitionStatus(currentCompetition);
            let competitionStatusColor = getCompetitionStatusColor(currentCompetition);

            // Second, format the competition using the interface
            const formattedCompetition: FormattedCompetition = {
                id: currentCompetition.id,
                startDate: currentCompetition.startDate ? formatStartEndDatesStrings(currentCompetition.startDate) : "",
                name: currentCompetition.name,
                createdBy: currentCompetition.createdBy ? await getCreatedBy(currentCompetition.createdBy) : "",
                status: competitionStatus,
                statusColor: competitionStatusColor,
                updatedOn: currentCompetition.updatedOn ? moment(currentCompetition.updatedOn).format("MM/DD/YY hh:mm a") : "",
                competition: currentCompetition
            };

            // Third, add the formatted competition to the list
            formattedCompetitions.push(formattedCompetition);
        }
        setFormattedCompetitions(formattedCompetitions);
    }

    const getCreatedBy = async (createdBy: string) => {
        const personQuery = await getPersonByPersonId(createdBy);
        if (personQuery.isSuccess) {
            const person: Person = personQuery.result;
            const personName = formatDisplayNameForUser(person);
            return personName;
        }
        return "";
    }

    const createCompetitionInvitation = async (selectedCompetition: FormattedCompetition) => {
        if (selectedCompetition && selectedCompetition.competition) {
            setIsLoading(true); 
            await generateAELCompetitionInvitationReport(selectedCompetition.competition, true);
            setIsLoading(false);
        }
        else setError("No competition found. Cannot generate report.");
    }

    return (
        <IonPage className="bg-light">
            <Header />
            <IonContent>
                <PageTitle title="My Competitions" />
                <IonRow className="ion-justify-content-center">
                    <IonCol size="12">
                        <IonCard mode="md" className="ion-padding bg-white">
                            <IonRow>
                                <IonCol size="12" className="ion-text-center">
                                    <Link
                                        to={`/index/ael/event/new`}
                                        className="link text-info"
                                    >
                                        <IonButton color="tertiary">
                                            Create New Competition
                                        </IonButton>
                                    </Link>
                                </IonCol>
                            </IonRow>
                            {isLoading ?
                                <Spinner />
                                :
                                <IonCardContent>
                                    {error && <ErrorBanner error={error} />}
                                    {(formattedCompetitions && formattedCompetitions.length > 0) ?
                                        <Table responsive striped hover>
                                            <thead>
                                                <tr>
                                                    <th>
                                                        <p className="ion-text-wrap">Date</p>
                                                    </th>
                                                    <th>
                                                        <p className="ion-text-wrap">Name</p>
                                                    </th>
                                                    <th>
                                                        <p className="ion-text-wrap">Created By</p>
                                                    </th>
                                                    <th>
                                                        <p className="ion-text-wrap">Status</p>
                                                    </th>
                                                    <th>
                                                        <p className="ion-text-wrap">Invitation</p>
                                                    </th>
                                                    <th>
                                                        <p className="ion-text-wrap">Last Updated</p>
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {formattedCompetitions.map((formattedCompetition, index) => (
                                                    <tr key={index}>
                                                        <td>
                                                            <p className="ion-text-wrap">{formattedCompetition.startDate}</p>
                                                        </td>
                                                        <td>
                                                            <p className="ion-text-wrap">{formattedCompetition.name}</p>
                                                        </td>
                                                        <td>
                                                            <p className="ion-text-wrap">{formattedCompetition.createdBy}</p>
                                                        </td>
                                                        <td>
                                                            <p className={("ion-text-wrap text-" + formattedCompetition.statusColor)}>{formattedCompetition.status}</p>
                                                        </td>
                                                        <td>
                                                            <p className="ion-text-wrap link">
                                                                <IonButton color="tertiary" onClick={() => createCompetitionInvitation(formattedCompetition)}>
                                                                    <p><IonIcon icon={mailSharp}/> Invitation</p>
                                                                </IonButton>
                                                            </p>
                                                        </td>
                                                        <td>
                                                            <p className="ion-text-wrap">{formattedCompetition.updatedOn}</p>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                        :
                                        <p>No competitions were found.</p>
                                    }
                                </IonCardContent>
                            }
                        </IonCard>
                    </IonCol>
                </IonRow>
            </IonContent>
        </IonPage>
    );
};

export default TeamCompetitionsPage;